import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { Container, Carousel } from '@pella/sash-framework';
import { CTA } from '../../';
import { contentstackClient } from '../../../utils/contentstack-client';
import { GET_ARTICLES_BY_TAXONOMY } from '../../../enums/contentstack-graphql';
import { isValidArray } from '../../utilities/validations';

import CardRenderer from '../card-types/card-renderer';

const ProjectAside = ({ data, dynamic, children, className, ...other }) => {
	const filters = isValidArray(dynamic?.filter_tags) ? dynamic.filter_tags.map((filter) => ({ taxonomies: { filter_by: { term_in: filter?.split(', ') } } })) : [];
	const { data: results, loading } = useQuery(GET_ARTICLES_BY_TAXONOMY, { client: contentstackClient, variables: { filters }, skip: !isValidArray(dynamic?.filter_tags) });

	if (!isValidArray(results?.all_articles?.items) && !isValidArray(dynamic?.manual_entries)) return null;

	return (
		<div
			className={`projectAside relative ${className || 'py-8 md:py-12'}`}
			{...other}
		>
			<Container>
				<div className='grid grid-cols-1 lg:grid-cols-[1fr_65%] lg:gap-x-10'>
					<div className={`col-start-1 ${isValidArray(dynamic.ctas) ? 'lg:self-end' : 'lg:self-center'}`}>
						{data.heading && (
							<h2
								className='mb-3'
								dangerouslySetInnerHTML={{ __html: data.heading }}
							/>
						)}
						{data.description && (
							<div
								className='text-subtitle mt-3'
								dangerouslySetInnerHTML={{ __html: data.description }}
							/>
						)}
					</div>

					{isValidArray(dynamic.ctas) && (
						<div className='col-start-1 row-start-2'>
							<CTA
								className='mt-3 max-lg:mb-2'
								data={dynamic.ctas[0]}
								size='sm'
							/>
						</div>
					)}

					<div className={`lg:col-start-2 ${dynamic.ctas?.[0] ? 'lg:row-span-2' : ''}`}>
						<Carousel
							slidesToShow={{ xs: 1, md: 2 }}
							slidePadding={16}
							className='py-4'
							showArrows
						>
							{loading && (
								<CardRenderer
									data={{
										card_type: 'standard',
										image: [{ image: { url: 'https://images.contentstack.io/v3/assets/bltf589e66bcaecd79c/bltf029c4eeac7a4e3f/611bd682cf33c613b2c06e04/352x198-Placeholder.png' } }],
										heading: `<span class="block h-5 bg-gray-10"></span> <span class="block h-5 bg-gray-10 w-1/2 mt-2"></span>`,
										description: `<span class="block h-4 bg-gray-10 mt-3"></span> <span class="block h-4 bg-gray-10 mt-2"></span> <span class="block h-4 bg-gray-10 w-1/2 mt-2"></span>`,
									}}
								/>
							)}
							{!loading &&
								isValidArray(results?.all_articles?.items) &&
								results.all_articles.items.map((project, index) => (
									<CardRenderer
										key={index}
										data={{
											card_type: 'standard',
											image: [{ image: project.seo?.thumbnailConnection?.edges?.[0]?.node }],
											heading: project.top_content?.heading,
											description: project.top_content?.subheading || project.seo?.description,
											ctas: [{ link: { href: project.url } }],
										}}
										showCtas={false}
									/>
								))}

							{!loading &&
								isValidArray(dynamic.manual_entries) &&
								dynamic.manual_entries.map((project, index) => (
									<CardRenderer
										key={index}
										data={{
											card_type: 'standard',
											image: project.modular_blocks?.find((block) => block.all_images != null)?.all_images?.image?.[0] ? project.modular_blocks.find((block) => block.all_images != null).all_images.image : project.seo?.thumbnail,
											heading: project.top_content?.heading,
											description: project.top_content?.subheading || project.seo?.description,
											ctas: [{ link: { href: project.url } }],
										}}
										showCtas={false}
									/>
								))}
						</Carousel>
					</div>
				</div>
			</Container>
		</div>
	);
};

ProjectAside.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string,
	data: PropTypes.object,
	dynamic: PropTypes.object,
};

export default ProjectAside;
